import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardContent,
  Divider,
  Typography,
} from "@material-ui/core";
import Loading from "../Loading/Loading";
import { ReviewTable, StarRating } from "./ReviewTable";
import {
  deleteCustomerReview,
  getProductCustomerReviews,
} from "../../services/ReviewService";
import "../../pages/Vendors/style.css";

const ProductCustomerReviews = ({ productId, productName }) => {
  const [loading, setLoading] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState({ total: 0, average: 0 });
  const [page, setPage] = useState({ page: 1 });
  const [pagination, setPagination] = useState({ last_page: 0 });

  const loadReviews = async (pageNumber) => {
    if (!productId) return;

    setLoading(true);
    try {
      const response = await getProductCustomerReviews(productId, pageNumber);

      if (response.data.success) {
        const payload = response.data.data;
        setReviews(payload.reviews?.data || []);
        setSummary(payload.summary || { total: 0, average: 0 });
        setPagination({ last_page: payload.reviews?.last_page || 0 });
      }
    } catch (error) {
      setReviews([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    setPage({ page: 1 });
  }, [productId]);

  useEffect(() => {
    if (productId) {
      loadReviews(page.page);
    }
  }, [productId, page]);

  const handleDelete = async (reviewId) => {
    const result = await deleteCustomerReview(reviewId);
    if (result?.success) {
      loadReviews(page.page);
    }
  };

  const next = () => {
    const currentPage = Number(page.page) + 1;
    setPage({ page: currentPage });
    window.scrollTo(0, 0);
  };

  const prev = () => {
    let currentPage = Number(page.page) - 1;
    if (currentPage === 0) {
      currentPage = 1;
    }
    setPage({ page: currentPage });
    window.scrollTo(0, 0);
  };

  const handlePageChange = (pageNumber) => {
    setPage({ page: pageNumber });
    window.scrollTo(0, 0);
  };

  const renderPagination = () => {
    const currentPage = Number(page.page);
    const lastPage = Number(pagination.last_page);
    const pagesToShow = 1;
    const paginationItems = [];

    if (currentPage !== 1) {
      paginationItems.push(
        <Button
          key={1}
          variant="outlined"
          className="pageLink"
          onClick={() => handlePageChange(1)}
          style={{ marginRight: "10px" }}
        >
          1
        </Button>
      );
    }

    if (currentPage > pagesToShow + 1) {
      paginationItems.push(<span key="ellipsis1">...</span>);
    }

    for (let i = Math.max(2, currentPage - pagesToShow); i < currentPage; i++) {
      paginationItems.push(
        <Button
          key={i}
          variant="outlined"
          className="pageLink"
          onClick={() => handlePageChange(i)}
          style={{ margin: "auto 10px" }}
        >
          {i}
        </Button>
      );
    }

    paginationItems.push(
      <Button
        key={currentPage}
        variant="contained"
        className="pageLink"
        disabled
        style={{ marginRight: "10px" }}
      >
        {currentPage}
      </Button>
    );

    for (
      let i = currentPage + 1;
      i <= Math.min(currentPage + pagesToShow, lastPage - 1);
      i++
    ) {
      paginationItems.push(
        <Button
          key={i}
          variant="outlined"
          className="pageLink"
          onClick={() => handlePageChange(i)}
          style={{ marginRight: "10px" }}
        >
          {i}
        </Button>
      );
    }

    if (lastPage - currentPage > pagesToShow) {
      paginationItems.push(<span key="ellipsis2">...</span>);
    }

    if (currentPage !== lastPage && lastPage > 0) {
      paginationItems.push(
        <Button
          key={lastPage}
          variant="outlined"
          className="pageLink"
          onClick={() => handlePageChange(lastPage)}
          style={{ margin: "auto 10px" }}
        >
          {lastPage}
        </Button>
      );
    }

    return paginationItems;
  };

  if (!productId) {
    return null;
  }

  return (
    <Card style={{ marginTop: 24 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Customer Reviews
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Reviews submitted by customers for{" "}
          <strong>{productName || "this product"}</strong>.
        </Typography>

        <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "16px 0" }}>
          <Typography variant="h4" style={{ margin: 0 }}>
            {summary.average > 0 ? summary.average.toFixed(1) : "0.0"}
          </Typography>
          <div>
            <StarRating rating={Math.round(summary.average)} />
            <Typography variant="caption" display="block" color="textSecondary">
              {summary.total} {summary.total === 1 ? "review" : "reviews"}
            </Typography>
          </div>
        </div>

        <Divider style={{ marginBottom: 16 }} />

        {loading ? (
          <Loading />
        ) : (
          <>
            <ReviewTable reviews={reviews} onDelete={handleDelete} />

            {pagination.last_page >= 2 ? (
              <nav>
                <ul className="paginationMenu">
                  <li className="menuItem">
                    <Button
                      variant="outlined"
                      disabled={Number(page.page) === 1}
                      className="pageLink"
                      onClick={prev}
                      style={{ marginRight: "10px" }}
                    >
                      Previous
                    </Button>
                  </li>
                  {renderPagination()}
                  <li className="menuItem">
                    <Button
                      variant="outlined"
                      className="pageLink"
                      disabled={Number(page.page) === Number(pagination.last_page)}
                      onClick={next}
                    >
                      Next
                    </Button>
                  </li>
                </ul>
              </nav>
            ) : null}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ProductCustomerReviews;
